/**
 * ScorecardNavigator - Dev Tool to view and test all scorecard types
 * 
 * Lists all scorecard entry forms in the app for comparison and optimization.
 * Access at: /dev/scorecards
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  Users, 
  Trophy, 
  Swords, 
  Mic,
  ChevronRight,
  ArrowLeft,
  Eye,
  Code
} from 'lucide-react';
import { Button } from '../ui/button';

const SCORECARDS = [
  {
    id: 'solo',
    name: 'SOLO_SCORECARD',
    displayName: 'Solo Practice',
    icon: ClipboardList,
    color: 'green',
    file: 'AddScorePage.jsx',
    usesComponent: 'ScoreEntryForm',
    testRoute: '/add-score',
    description: 'Single player score entry with voice, scanner, and manual input.',
    features: [
      'Voice input',
      'Scorecard scanner (AI)',
      'Hole-by-hole ScoreBox',
      'Total score quick entry',
      'Draft auto-save',
      'Handicap calculation'
    ]
  },
  {
    id: 'skins',
    name: 'SKINS_SCORECARD',
    displayName: 'Skins Game',
    icon: Trophy,
    color: 'amber',
    file: 'SkinsEventDetail.jsx',
    usesComponent: 'ScoreEntryForm',
    testRoute: null, // Requires event ID
    description: 'Multi-player competition where lowest score wins each hole.',
    features: [
      'Multi-player entry',
      'Voice input',
      'Scorecard scanner',
      'Skin winner per hole',
      'Carryover tracking',
      'Settlement calculation',
      'Draft auto-save'
    ]
  },
  {
    id: 'stroke',
    name: 'STROKE_PLAY_SCORECARD',
    displayName: 'Stroke Play',
    icon: Users,
    color: 'blue',
    file: 'StrokePlayEventDetail.jsx',
    usesComponent: 'ScoreEntryForm',
    testRoute: null, // Requires event ID
    description: 'Tournament-style scoring with leaderboard and handicaps.',
    features: [
      'Multi-player entry',
      'Voice input',
      'Scorecard scanner',
      'Live leaderboard',
      'Net vs Gross scoring',
      'Handicap strokes/hole',
      'Draft auto-save'
    ]
  },
  {
    id: 'match',
    name: 'MATCH_PLAY_SCORECARD',
    displayName: 'Match Play',
    icon: Swords,
    color: 'purple',
    file: 'MatchPlayEventDetail.jsx',
    usesComponent: 'ScoreEntryForm',
    testRoute: null, // Requires event ID
    description: 'Team-based competition (2v2 or 1v1) with hole-by-hole winners.',
    features: [
      'Team scoring (2v2)',
      '9-hole focused',
      'Hole winner tracking',
      'Match status (2UP, AS)',
      'Nassau format support',
      'Draft auto-save'
    ]
  },
  {
    id: 'quickcard',
    name: 'QUICKCARD_SCORECARD',
    displayName: 'QuickCard (Group Round)',
    icon: Mic,
    color: 'emerald',
    file: 'QuickCard.jsx',
    usesComponent: 'ScoreBox',
    testRoute: '/test-quickcard',
    description: 'Voice-powered group scoring for casual rounds.',
    features: [
      'Voice group entry',
      'AI readback confirmation',
      'Real-time multi-player',
      'Live scorecard display',
      'Manual ScoreBox entry',
      'Best score highlighting',
      'Skin tracking (optional)'
    ]
  }
];

const ScorecardNavigator = () => {
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState(null);

  const getColorClasses = (color) => {
    const colors = {
      green: 'bg-green-500/20 text-green-400 border-green-500/30',
      amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
    };
    return colors[color] || colors.green;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold">Scorecard Navigator</h1>
          <p className="text-sm text-gray-400">View and compare all scorecard types</p>
        </div>
      </div>

      {/* Scorecard List */}
      <div className="space-y-3">
        {SCORECARDS.map((card) => {
          const Icon = card.icon;
          const isSelected = selectedCard?.id === card.id;
          
          return (
            <div key={card.id}>
              {/* Card Header */}
              <button
                onClick={() => setSelectedCard(isSelected ? null : card)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                  isSelected 
                    ? `${getColorClasses(card.color)} border-opacity-100` 
                    : 'bg-gray-900 border-gray-800 hover:border-gray-700'
                }`}
              >
                <div className={`p-3 rounded-lg ${getColorClasses(card.color)}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold">{card.displayName}</p>
                  <p className="text-xs text-gray-500 font-mono">{card.name}</p>
                </div>
                <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${
                  isSelected ? 'rotate-90' : ''
                }`} />
              </button>
              
              {/* Expanded Details */}
              {isSelected && (
                <div className="mt-2 p-4 bg-gray-900/50 rounded-xl border border-gray-800 space-y-4">
                  <p className="text-sm text-gray-300">{card.description}</p>
                  
                  {/* File Info */}
                  <div className="flex items-center gap-2 text-xs">
                    <Code className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-500">File:</span>
                    <span className="font-mono text-blue-400">{card.file}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500">Uses:</span>
                    <span className="font-mono text-purple-400">{card.usesComponent}</span>
                  </div>
                  
                  {/* Features */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Features:</p>
                    <div className="flex flex-wrap gap-2">
                      {card.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          className="text-xs px-2 py-1 bg-gray-800 rounded-full text-gray-300"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    {card.testRoute && (
                      <Button
                        onClick={() => navigate(card.testRoute)}
                        className="flex-1 bg-blue-600 hover:bg-blue-500"
                        size="sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Live
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        // This could open the file in an editor or show more details
                        navigator.clipboard.writeText(`/app/frontend/src/components/${card.file}`);
                        alert(`File path copied: /app/frontend/src/components/${card.file}`);
                      }}
                      variant="outline"
                      className="flex-1 border-gray-700"
                      size="sm"
                    >
                      <Code className="w-4 h-4 mr-2" />
                      Copy Path
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-8 p-4 bg-gray-900 rounded-xl border border-gray-800">
        <h3 className="font-bold mb-2">Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Total Scorecards</p>
            <p className="text-2xl font-bold">{SCORECARDS.length}</p>
          </div>
          <div>
            <p className="text-gray-500">Shared Component</p>
            <p className="text-lg font-mono text-purple-400">ScoreBox</p>
          </div>
        </div>
        <div className="mt-4 text-xs text-gray-500">
          <p>All scorecards use ScoreBox from ScoreEntryForm.jsx for manual entry.</p>
          <p className="mt-1">Goal: Optimize each scorecard while maintaining unique features.</p>
        </div>
      </div>
    </div>
  );
};

export default ScorecardNavigator;
